import './styles.scss'

import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import ReactSelect, { components } from 'react-select'
import Tooltip from 'rc-tooltip'
import PropTypes from 'prop-types'

import TimeZoneIcon from 'lib/icons/time-zone'
import railsContext from 'lib/selectors/rails-context'
import I18n from 'lib/i18n'

const { Option, SingleValue } = components

const CustomOptionForCustomTimeZoneDisplay = props => (
  <div>
    <Option {...props}>
      <div className='ellipsis'>
        <span>{props.children}</span>
      </div>
    </Option>
  </div>
)

const CustomOption = props => {
  const {
    children,
    data: { label }
  } = props

  return (
    <Option {...props}>
      <Tooltip trigger={['hover']} placement='right' overlay={label} mouseEnterDelay={0.5}>
        <div className='ellipsis'>
          <span>{children}</span>
        </div>
      </Tooltip>
    </Option>
  )
}

const CustomSingleValueForCustomTimeZoneDisplay = ({ children, ...props }) => {
  const match = children.match(/^(\(GMT\s*[+-]\d{2}:\d{2}\))\s+(.+)$/)

  if (match) {
    const gmtPart = match[1]
    const cityPart = match[2]

    return (
      <SingleValue {...props}>
        <div className='timezone-display'>
          <div className='gmt-offset'>{gmtPart}</div>
          <div className='city-name'>{cityPart}</div>
        </div>
      </SingleValue>
    )
  }

  return (
    <SingleValue {...props}>
      <div className='timezone-display'>{children}</div>
    </SingleValue>
  )
}

const CustomSingleValue = (props, withIcon) => (
  <SingleValue {...props}>
    {withIcon && <TimeZoneIcon />}
    {props.children}
  </SingleValue>
)

const customStyles = customTimeZoneDisplay => ({
  control: (provided, { isFocused }) => ({
    width: '254px',
    height: '10px',
    border: '1px solid #bbb',
    fontSize: '13px',
    minHeight: isFocused ? '30px' : '30px',
    display: 'inline-flex'
  }),
  menu: provided => ({
    ...provided,
    marginTop: 2,
    width: '254px',
    paddingTop: '0',
    paddingBottom: '0'
  }),
  menuList: provided => ({
    ...provided,
    paddingTop: '0',
    paddingBottom: '0'
  }),
  indicatorSeparator: provided => ({
    ...provided,
    display: 'none'
  }),
  dropdownIndicator: (provided, props) => ({
    ...provided,
    transform: props.selectProps.menuIsOpen && customTimeZoneDisplay ? 'rotate(180deg)' : 'unset'
  }),
  singleValue: provided => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  })
})

const TimeZonePicker = ({ timezone, onChange, customTimeZoneDisplay, withIcon = false }) => {
  const selectRef = useRef(null)

  const { timezoneNames } = useSelector(railsContext)

  return (
    <div data-attr='timezone-select'>
      <ReactSelect
        ref={selectRef}
        components={{
          Option: props => (customTimeZoneDisplay ? CustomOptionForCustomTimeZoneDisplay(props) : CustomOption(props)),
          ...(customTimeZoneDisplay
            ? { SingleValue: CustomSingleValueForCustomTimeZoneDisplay }
            : { SingleValue: props => CustomSingleValue(props, withIcon) })
        }}
        value={timezoneNames.filter(option => option.value === timezone)}
        isSearchable={!customTimeZoneDisplay}
        options={timezoneNames}
        placeholder={I18n.t('timezone.placeholder')}
        onChange={option => onChange(option.value)}
        className='react-select-container'
        classNamePrefix='react-select'
        noOptionsMessage={() => I18n.t('timezone.no_options')}
        styles={customStyles(customTimeZoneDisplay)}
        theme={theme => ({
          ...theme,
          borderRadius: 0,
          colors: {
            ...theme.colors,
            primary25: 'rgba(142, 202, 16, 0.2)',
            primary: '#8eca10'
          }
        })}
      />
    </div>
  )
}

CustomOptionForCustomTimeZoneDisplay.propTypes = {
  data: PropTypes.object,
  children: PropTypes.node,
  isSelected: PropTypes.bool,
  selectProps: PropTypes.object
}

CustomOption.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object
}

CustomSingleValueForCustomTimeZoneDisplay.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object
}

CustomSingleValue.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object
}

TimeZonePicker.propTypes = {
  timezone: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  customTimeZoneDisplay: PropTypes.bool,
  withIcon: PropTypes.bool
}

export default TimeZonePicker
